import { Navigate } from "react-router-dom";
import RouteGuard from "../guards/RouteGuard";
import AuthGuard from "../components/authentication/AuthGuard";
import GuestGuard from "../components/authentication/GuestGuard";
import DashboardLayout from "../components/Layouts/DashboardLayout";
import { LoginPortal } from "../pages/Authentication/LoginPortal";
import ResetPassword from "../pages/Authentication/ResetPassword/ResetPassword";

import LazyLoader from "./routes-loader";
import { ROUTES } from "./route.constants";

const routes = [
  {
    path: "/",
    element: <Navigate to="login" />,
  },
  {
    path: ROUTES.AUTH.LOGIN,
    element: (
      <GuestGuard>
        <LoginPortal />
      </GuestGuard>
    ),
  },
  {
    path: ROUTES.AUTH.FORGOT_PASSWORD,
    element: (
      <GuestGuard>
        <LoginPortal />
      </GuestGuard>
    ),
  },
  {
    path: ROUTES.AUTH.RESET_PASSWORD,
    element: (
      <GuestGuard>
        <ResetPassword />
      </GuestGuard>
    ),
  },
  {
    path: ROUTES.DASHBOARD.PATH,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <LazyLoader.DashboardDashboard />,
      },
    ],
  },
  {
    path: ROUTES.AUTH.FORBIDDEN,
    element: <LazyLoader.Error403 />,
  },
  {
    path: "*",
    element: <LazyLoader.Error404 />,
  },

  /**
   * INVENTORY
   */
  {
    path: ROUTES.INVENTORY.PATH,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      // purchase orders
      {
        path: ROUTES.INVENTORY.PURCHASE_ORDERS,
        element: <LazyLoader.PurchaseOrders />,
      },
      {
        path: ROUTES.INVENTORY.CREATE_PURCHASE_ORDER,
        element: <LazyLoader.PurchaseOrderForm />,
      },
      {
        path: ROUTES.INVENTORY.PURCHASE_ORDER_DETAILS,
        element: <LazyLoader.PurchaseOrderDetails />,
      },

      // inventory received
      {
        path: ROUTES.INVENTORY.INVENTORY_RECEIVED,
        element: <LazyLoader.InventoryReceived />,
      },
      {
        path: ROUTES.INVENTORY.INVENTORY_RECEIVED_DETAILS,
        element: <LazyLoader.InventoryReceivedDetails />,
      },
      {
        path: ROUTES.INVENTORY.EDIT_INVENTORY_RECEIVED,
        element: <LazyLoader.EditInventoryReceived />,
      },

      // sales
      {
        path: ROUTES.INVENTORY.SALES,
        element: <LazyLoader.Sales />,
      },
      {
        path: ROUTES.INVENTORY.CREATE_SALE,
        element: <LazyLoader.SaleForm />,
      },
      {
        path: ROUTES.INVENTORY.CHECKOUT_SALE,
        element: <LazyLoader.CheckoutSale />,
      },
      {
        path: ROUTES.INVENTORY.SALE_SUCCESSFUL,
        element: <LazyLoader.SaleSuccessful />,
      },
      {
        path: ROUTES.INVENTORY.SALE_DETAILS,
        element: <LazyLoader.SaleDetails />,
      },

      // credit management
      {
        path: ROUTES.INVENTORY.CREDIT_MANAGEMENT,
        element: <LazyLoader.CreditManagement />,
      },

      {
        path: ROUTES.INVENTORY.EXPENSES_MANAGEMENT,
        element: <LazyLoader.ExpensesManagement />,
      },
    ],
  },

  {
    path: ROUTES.REPORTS.PATH,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      /**
       * CREDIT MANAGEMENT
       */
      {
        path: ROUTES.REPORTS.CREDIT_MANAGEMENT.PATH,
        requiresAuth: true,
        children: [
          {
            path: ROUTES.REPORTS.CREDIT_MANAGEMENT.UNPAID_INVOICES,
            requiresAuth: true,
            element: <LazyLoader.UnpaidInvoicesReport />,
          },
          {
            path: ROUTES.REPORTS.CREDIT_MANAGEMENT.CREDIT_ACTIVITY,
            requiresAuth: true,
            element: <LazyLoader.CreditActivityReport />,
          },
          {
            path: ROUTES.REPORTS.CREDIT_MANAGEMENT.PAYMENT_TRENDS,
            requiresAuth: true,
            element: <LazyLoader.PaymentTrendsReport />,
          },
          {
            path: ROUTES.REPORTS.CREDIT_MANAGEMENT.VISUALIZATION,
            requiresAuth: true,
            element: <LazyLoader.CreditManagementVisualizationReport />,
          },
        ],
      },
      {
        path: ROUTES.REPORTS.EXPENSES.PATH,
        requiresAuth: true,
        children: [
          {
            path: ROUTES.REPORTS.EXPENSES.REGISTER,
            requiresAuth: true,
            element: <LazyLoader.ExpensesRegisterReport />,
          },
          {
            path: ROUTES.REPORTS.EXPENSES.CATEGORY_SUMMARY,
            requiresAuth: true,
            element: <LazyLoader.ExpensesCategorySummaryReport />,
          },
          {
            path: ROUTES.REPORTS.EXPENSES.CASH_ACCOUNT_SUMMARY,
            requiresAuth: true,
            element: <LazyLoader.ExpensesCategoryCashAccountReport />,
          },
          {
            path: ROUTES.REPORTS.EXPENSES.VISUALIZATION,
            requiresAuth: true,
            element: <LazyLoader.ExpenseVisualizationReport />,
          },
        ],
      },
    ],
  },

  /**
   * CONFIGURATION
   */
  {
    path: ROUTES.CONFIGURATION.PATH,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: ROUTES.CONFIGURATION.ITEMS,
        element: <LazyLoader.Items />,
      },
      {
        path: ROUTES.CONFIGURATION.ITEM_DETAILS,
        element: <LazyLoader.ItemDetails />,
      },
      {
        path: ROUTES.CONFIGURATION.CUSTOMERS,
        element: <LazyLoader.Customers />,
      },
      {
        path: ROUTES.CONFIGURATION.CUSTOMER_DETAILS,
        element: <LazyLoader.CustomerDetails />,
      },
      {
        path: ROUTES.CONFIGURATION.SUPPLIERS,
        element: <LazyLoader.Suppliers />,
      },
      {
        path: ROUTES.CONFIGURATION.ITEM_CATEGORIES,
        element: <LazyLoader.ItemCategories />,
      },
      {
        path: ROUTES.CONFIGURATION.BRANCHES,
        element: <LazyLoader.Branches />,
      },
      {
        path: ROUTES.CONFIGURATION.STAFF,
        element: <LazyLoader.Staff />,
      },
      {
        path: ROUTES.CONFIGURATION.PAYMENT_ACCOUNTS,
        element: <LazyLoader.PaymentAccounts />,
      },
      {
        path: ROUTES.CONFIGURATION.EXPENSES_CATEGORIES,
        element: <LazyLoader.ExpensesCategories />,
      },
    ],
  },

  /**
   * ADMIN ROUTES
   */
  {
    path: ROUTES.ADMIN.PATH,
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <LazyLoader.AdminDashboard />,
      },
      {
        path: ROUTES.ADMIN.DASHBOARD,
        element: <LazyLoader.AdminDashboard />,
      },
      {
        path: ROUTES.ADMIN.BUSINESSES,
        element: <LazyLoader.Businesses />,
      },
      {
        path: ROUTES.ADMIN.BUSINESS_ADMINS,
        element: <LazyLoader.BusinessAdmins />,
      },
    ],
  },
];

export default routes;
